import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../layout/Layout';
import { formatDate, formatDateAsItIs } from '../helper/common';
import { imagePath } from '../helper/image';
import timeZoneData from '../helper/timeZoneData.json';
import { Tab, Tabs, Nav, Table } from 'react-bootstrap';
import CustomPagination from '../components/CustomPagination';
import ScrollingTab from '../components/ScrollingTab';
import CustomFormInput from '../components/CustomFormInput';
import { requestHandler } from '../helper';
import { updateEventRouteLinks } from '../controller';
import { toast } from 'react-toastify';

const MAX_ROUTE_LINKS = 10;
const JOIN_SETTING_LABELS = {
    open: 'Open (anyone can join)',
    request: 'Request to join (approval needed)',
    invite: 'Invite only',
};
const joinSettingLabel = (value) => JOIN_SETTING_LABELS[value] || (value ? value : '-');
// AO 2.0 (event hosting): co-host Crews and who a private Crew event is for
const CO_HOST_STATUS_BADGES = { pending: 'bg-warning text-dark', accepted: 'bg-success text-white', declined: 'bg-secondary text-white', left: 'bg-secondary text-white', removed: 'bg-danger text-white' };
const coHostCrewsOf = (event) => (Array.isArray(event?.secondaryCrews) ? event.secondaryCrews : []);
const audienceLabel = (event) => (event?.audience === 'crew' ? 'Crew members' : 'Specific people');
const personName = (user) => `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.userName || user?._id || '-';

function EventDetails() {
    const location = useLocation();
    const navigate = useNavigate();
    const eventData = location.state?.eventData;

    // Determine if this is crew or company data
    const isCrew = eventData?.timeZone && !eventData?.eventDate; // Crews have timeZone but no eventDate
    const isCompany = eventData?.eventDate && !eventData?.timeZone; // Companies have eventDate but no timeZone

    // Crew people: owner first, then admins, then members (read only)
    const crewMemberRows = isCrew ? [
        ...(eventData.ownerId ? [{ ...eventData.ownerId, crewRole: 'owner' }] : []),
        ...(eventData.members || []).map(m => ({
            ...m,
            crewRole: (eventData.adminIds || []).some(id => String(id?._id || id) === String(m._id)) ? 'admin' : 'member',
        })).sort((a, b) => (a.crewRole === 'admin' ? 0 : 1) - (b.crewRole === 'admin' ? 0 : 1)),
    ] : [];
    const crewRequestRows = isCrew && Array.isArray(eventData.joinRequests) ? eventData.joinRequests : [];

    // Route and Map Links (events only): editable here, saved straight to the event
    const [routeLinks, setRouteLinks] = useState(Array.isArray(eventData?.routeLinks) ? eventData.routeLinks : []);
    const [editingLinks, setEditingLinks] = useState(false);
    const [draftLinks, setDraftLinks] = useState([]);
    const [savingLinks, setSavingLinks] = useState(false);

    useEffect(() => {
        setRouteLinks(Array.isArray(eventData?.routeLinks) ? eventData.routeLinks : []);
        setEditingLinks(false);
    }, [eventData]);

    const startEditLinks = () => {
        setDraftLinks(routeLinks.map(({ url, label }) => ({ url: url || '', label: label || '' })));
        setEditingLinks(true);
    };
    const changeDraftLink = (index, key, value) => {
        setDraftLinks((links) => links.map((link, i) => (i === index ? { ...link, [key]: value } : link)));
    };
    const saveLinks = async () => {
        const cleaned = draftLinks
            .map(({ url, label }) => ({ url: url.trim(), label: label.trim() }))
            .filter(({ url, label }) => url || label);
        if (cleaned.some(({ url }) => !url)) {
            toast.error('Please enter a link for every row, or remove the empty ones');
            return;
        }
        await requestHandler(
            async () => await updateEventRouteLinks(eventData._id, cleaned),
            setSavingLinks,
            (res) => {
                toast.success(res.message);
                setRouteLinks(res.data?.routeLinks || cleaned);
                setEditingLinks(false);
            },
            () => { }
        );
    };

    // Pagination states for all tabs
    const [membersPagination, setMembersPagination] = useState({ page: 1, limit: 10, totalPages: 1, totalData: 0 });
    const [crewTab, setCrewTab] = useState('members');
    const [requestsPagination, setRequestsPagination] = useState({ page: 1, limit: 10, totalPages: 1, totalData: 0 });
    const [staffPagination, setStaffPagination] = useState({ page: 1, limit: 10, totalPages: 1, totalData: 0 });
    const [waitlistPagination, setWaitlistPagination] = useState({ page: 1, limit: 10, totalPages: 1, totalData: 0 });
    const [questionsPagination, setQuestionsPagination] = useState({ page: 1, limit: 10, totalPages: 1, totalData: 0 });

    // Update pagination when eventData changes
    useEffect(() => {
        if (eventData) {
            // Crews list the owner first, who is not part of `members`
            const membersCount = (Array.isArray(eventData.members) ? eventData.members.length : 0) + (isCrew && eventData.ownerId ? 1 : 0);
            const requestsCount = Array.isArray(eventData.joinRequests) ? eventData.joinRequests.length : 0;
            setRequestsPagination(prev => ({
                ...prev,
                totalData: requestsCount,
                totalPages: Math.ceil(requestsCount / prev.limit),
                page: 1
            }));
            const staffCount = coHostCrewsOf(eventData).length;
            const waitlistCount = Array.isArray(eventData.waitlist) ? eventData.waitlist.length : 0;
            const questionsCount = Array.isArray(eventData.questions) ? eventData.questions.length : 0;

            setMembersPagination(prev => ({
                ...prev,
                totalData: membersCount,
                totalPages: Math.ceil(membersCount / prev.limit),
                page: 1
            }));

            if (isCompany) {
                setStaffPagination(prev => ({
                    ...prev,
                    totalData: staffCount,
                    totalPages: Math.ceil(staffCount / prev.limit),
                    page: 1
                }));

                setWaitlistPagination(prev => ({
                    ...prev,
                    totalData: waitlistCount,
                    totalPages: Math.ceil(waitlistCount / prev.limit),
                    page: 1
                }));

                setQuestionsPagination(prev => ({
                    ...prev,
                    totalData: questionsCount,
                    totalPages: Math.ceil(questionsCount / prev.limit),
                    page: 1
                }));
            }
        }
    }, [eventData, isCompany]);

    // const getImageUrl = (imgPath) => {
    //     return `${imagePath}${imgPath}`;
    // };

    const getTimeZoneText = (timezoneId) => {
        if (!timezoneId) return '-';
        const timezone = timeZoneData.find(tz => tz.id === parseInt(timezoneId));
        return timezone ? timezone.text : timezoneId;
    };

    if (!eventData) {
        return (
            <Layout>
                <div className="card-wrapper">
                    <div className="card">
                        <div className="card-body text-center">
                            <button
                                className="back-arrow-btn"
                                onClick={() => navigate('/marketplace-management')}
                                title="Back to Marketplace"
                            >
                                <img src="/images/svg/back-arrow.svg" alt="back" />
                            </button>
                            <h4>Event not found</h4>
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="card-wrapper bg-transparent">
                <div className="card-header border-0 px-0">
                    <div className="d-flex align-items-center gap-3">
                        <button
                            className="back-arrow-btn"
                            onClick={() => navigate('/marketplace-management')}
                            title="Back to Marketplace"
                        >
                            <img src="/images/svg/back-arrow.svg" alt="back" />
                        </button>
                        <h4 className="mb-0 title-text">{eventData?.name || (isCrew ? 'Crew Details' : 'Event Details')}</h4>
                    </div>
                </div>
                <div className="card-body p-0">
                    <div className="row g-3">
                        <div className='col-12'>
                            <div className='card border-0 rounded-12px'>
                                <div className='card-header bg-transparent'>
                                    <h4 className='fw-bold'>Infromation</h4>
                                </div>
                                <div className='card-body'>
                                    <div className="row">
                                        <div className="col-lg-6">
                                            <div className='d-flex mb-2'>
                                                <h6 className='event-detail-title fw-bold'>{isCrew ? 'Crew Name' : 'Event Name'}</h6>
                                                <p className='mb-0 text-light'>{eventData?.name || '-'}</p>
                                            </div>
                                            {isCompany && (
                                                <>
                                                    <div className='d-flex mb-2'>
                                                        <h6 className='event-detail-title fw-bold'>Event Date</h6>
                                                        <p className='mb-0 text-light'>{eventData?.eventDate ? formatDate(eventData.eventDate) : '-'}</p>
                                                    </div>
                                                    <div className='d-flex mb-2'>
                                                        <h6 className='event-detail-title fw-bold'>Capacity</h6>
                                                        <p className='mb-0 text-light'>{eventData?.capacity ?? '-'}</p>
                                                    </div>
                                                    <div className='d-flex mb-2'>
                                                        <h6 className='event-detail-title fw-bold'>Time Zone</h6>
                                                        <p className='mb-0 text-light'>{getTimeZoneText(eventData?.eventTimezoneID)}</p>
                                                    </div>
                                                    <div className='d-flex mb-2'>
                                                        <h6 className='event-detail-title fw-bold'>Activity</h6>
                                                        <p className='mb-0 text-light'>{eventData?.activities !== 0 ? eventData?.activities?.map(activity => activity).join(', ') : "-"}</p>
                                                    </div>
                                                    <div className='d-flex mb-2'>
                                                        <h6 className='event-detail-title fw-bold'>Event Type</h6>
                                                        <p className='mb-0 text-light'>{eventData?.eventType === 1 ? "Public" : "Private"}</p>
                                                    </div>
                                                </>
                                            )}
                                            {isCompany && eventData?.primaryCrewId?.name && (
                                                <>
                                                    <div className='d-flex mb-2'>
                                                        <h6 className='event-detail-title fw-bold'>Host Crew</h6>
                                                        <p className='mb-0 text-light'>{eventData.primaryCrewId.name}</p>
                                                    </div>
                                                    <div className='d-flex mb-2'>
                                                        <h6 className='event-detail-title fw-bold'>Co-host Crews</h6>
                                                        <p className='mb-0 text-light'>
                                                            {coHostCrewsOf(eventData).filter(h => h.status === 'accepted' && h.crewId?.name).map(h => h.crewId.name).join(', ') || '-'}
                                                        </p>
                                                    </div>
                                                    {eventData?.eventType === 0 && (
                                                        <div className='d-flex mb-2'>
                                                            <h6 className='event-detail-title fw-bold'>Audience</h6>
                                                            <p className='mb-0 text-light'>{audienceLabel(eventData)}</p>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                            {isCrew && (
                                                <>
                                                    <div className='d-flex mb-2'>
                                                        <h6 className='event-detail-title fw-bold'>Join Setting</h6>
                                                        <p className='mb-0 text-light'>{joinSettingLabel(eventData?.joinType)}</p>
                                                    </div>
                                                    <div className='d-flex mb-2'>
                                                        <h6 className='event-detail-title fw-bold'>Time Zone</h6>
                                                        <p className='mb-0 text-light'>{getTimeZoneText(eventData?.timeZone)}</p>
                                                    </div>
                                                    <div className='d-flex mb-2'>
                                                        <h6 className='event-detail-title fw-bold'>Category</h6>
                                                        <p className='mb-0 text-light'>{eventData?.category || '-'}</p>
                                                    </div>
                                                    <div className='d-flex mb-2'>
                                                        <h6 className='event-detail-title fw-bold'>Main Activity</h6>
                                                        <p className='mb-0 text-light'>{eventData?.mainActivity?.title || '-'}</p>
                                                    </div>
                                                    <div className='d-flex mb-2'>
                                                        <h6 className='event-detail-title fw-bold'>Home Location</h6>
                                                        <p className='mb-0 text-light'>{eventData?.homeLocation?.name || '-'}</p>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        <div className='col-lg-6'>
                                            <div className='left-border ps-lg-3'>
                                                <div className='d-flex mb-2'>
                                                    <h6 className='event-detail-title fw-bold'>Owner</h6>
                                                    <p className='mb-0 text-light'>
                                                        {eventData?.ownerId
                                                            ? `${eventData.ownerId.firstName || ''} ${eventData.ownerId.lastName || ''}`.trim() || eventData.ownerId.userName || '-'
                                                            : '-'}
                                                    </p>
                                                </div>
                                                {isCompany && (
                                                    <>
                                                        <div className='d-flex mb-2'>
                                                            <h6 className='event-detail-title fw-bold'>Location</h6>
                                                            <p className='mb-0 text-light'>{eventData?.location || '-'}</p>
                                                        </div>
                                                        <div className='d-flex mb-2'>
                                                            <h6 className='event-detail-title fw-bold'>Staff Limit</h6>
                                                            <p className='mb-0 text-light'>{eventData?.staffLimit ?? '-'}</p>
                                                        </div>
                                                    </>
                                                )}
                                                <div className='d-flex mb-2'>
                                                    <h6 className='event-detail-title fw-bold'>Created</h6>
                                                    <p className='mb-0 text-light'>{eventData?.createdAt ? formatDateAsItIs(eventData.createdAt) : '-'}</p>
                                                </div>
                                                {isCompany && (
                                                    <>
                                                        <div className='d-flex mb-2'>
                                                            <h6 className='event-detail-title fw-bold'>Skills</h6>
                                                            <p className='mb-0 text-light'>{eventData?.skill !== 0 ? eventData?.skill?.map(akill => akill).join(', ') : "-"}</p>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='col-12'>
                            <div className="row">
                                <div className="col-lg-6">
                                    <div className="card border-0 rounded-12px h-100">
                                        <div className='card-header bg-transparent '>
                                            <h4 className='fw-bold'>Description</h4>
                                        </div>
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col-8">
                                                    <p className='mb-0 text-light'>{eventData?.description || '-'}</p>
                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className='col-lg-6'>
                                    <div className="card border-0 rounded-12px">
                                        <div className='card-header bg-transparent '>
                                            <h4 className='fw-bold'>Image</h4>
                                        </div>
                                        <div className="card-body">
                                            <div className={isCrew ? 'col-12' : 'col-4'}>
                                                {isCompany && eventData?.eventImage?.url ? (
                                                    <img
                                                        src={imagePath() + eventData?.eventImage?.url}
                                                        alt="Event"
                                                        title="Event Image"
                                                        className='marketplace-image'
                                                    />
                                                ) : isCrew ? (
                                                    <div className="d-flex flex-wrap gap-3">
                                                        {[
                                                            { label: 'Crew Photo', image: eventData?.crewImage },
                                                            { label: 'Cover Photo', image: eventData?.coverImage },
                                                        ].map(({ label, image }) => (
                                                            <div key={label}>
                                                                <h6 className='fw-bold mb-2'>{label}</h6>
                                                                {image?.url ? (
                                                                    <img
                                                                        src={imagePath() + image.url}
                                                                        alt={label}
                                                                        title={label}
                                                                        className='marketplace-image'
                                                                    />
                                                                ) : (
                                                                    <p className="text-muted mb-0">Not added</p>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="text-muted text-center">
                                                        <i className="fas fa-image fa-3x mb-2"></i>
                                                        <p>No image available</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {isCompany && (
                                <div className="card border-0 rounded-12px mt-3">
                                    <div className='card-header bg-transparent d-flex align-items-center justify-content-between gap-2'>
                                        <h4 className='fw-bold mb-0'>Route and Map Links</h4>
                                        {!editingLinks && (
                                            <button type="button" className="btn btn-outline-light btn-sm" onClick={startEditLinks}>
                                                Edit
                                            </button>
                                        )}
                                    </div>
                                    <div className="card-body">
                                        {editingLinks ? (
                                            <>
                                                {draftLinks.length === 0 && <p className='text-light'>No links. Use Add link to create one.</p>}
                                                {draftLinks.map((link, index) => (
                                                    <div key={index} className='row g-2 align-items-end mb-3'>
                                                        <div className='col-md-4'>
                                                            <CustomFormInput
                                                                label="Label"
                                                                id={`routeLabel${index}`}
                                                                placeholder="e.g. AllTrails"
                                                                maxLength={40}
                                                                value={link.label}
                                                                onChange={(e) => changeDraftLink(index, 'label', e.target.value)}
                                                                noThemeClass
                                                            />
                                                        </div>
                                                        <div className='col-md-6'>
                                                            <CustomFormInput
                                                                label="Link"
                                                                id={`routeUrl${index}`}
                                                                placeholder="https://..."
                                                                value={link.url}
                                                                onChange={(e) => changeDraftLink(index, 'url', e.target.value)}
                                                                noThemeClass
                                                            />
                                                        </div>
                                                        <div className='col-md-2'>
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-light w-100"
                                                                onClick={() => setDraftLinks((links) => links.filter((_, i) => i !== index))}
                                                            >
                                                                Remove
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                                <div className="d-flex flex-wrap align-items-center gap-2">
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-light"
                                                        disabled={draftLinks.length >= MAX_ROUTE_LINKS}
                                                        onClick={() => setDraftLinks((links) => [...links, { url: '', label: '' }])}
                                                    >
                                                        Add link
                                                    </button>
                                                    <small className="text-light">{draftLinks.length}/{MAX_ROUTE_LINKS}</small>
                                                    <div className="ms-auto d-flex gap-2">
                                                        <button type="button" className="btn btn-outline-light" disabled={savingLinks} onClick={() => setEditingLinks(false)}>
                                                            Cancel
                                                        </button>
                                                        <button type="button" className="btn btn-primary" disabled={savingLinks} onClick={saveLinks}>
                                                            Save Links
                                                        </button>
                                                    </div>
                                                </div>
                                            </>
                                        ) : routeLinks.length > 0 ? (
                                            routeLinks.map((link, index) => (
                                                <div key={index} className='d-flex mb-2'>
                                                    <h6 className='event-detail-title fw-bold'>{link.label || 'Link'}</h6>
                                                    <a href={link.url} target="_blank" rel="noopener noreferrer" className='mb-0 text-break'>{link.url}</a>
                                                </div>
                                            ))
                                        ) : (
                                            <p className='mb-0 text-light'>No route or map links added.</p>
                                        )}
                                    </div>
                                </div>
                            )}

                        </div>
                        <div className='col-12'>
                            <div className="card border-0 rounded-12px">
                                <div className='card-body'>
                                    {isCrew ? (
                                        // For Crews - Members and pending join requests, same tab and table style as User Management
                                        <div>
                                            <Tab.Container activeKey={crewTab} onSelect={(key) => key && setCrewTab(key)}>
                                                <ScrollingTab>
                                                    <Nav className='theme-tab'>
                                                        <Nav.Item>
                                                            <Nav.Link eventKey="members">Members ({crewMemberRows.length})</Nav.Link>
                                                        </Nav.Item>
                                                        <Nav.Item>
                                                            <Nav.Link eventKey="requests">Requests ({crewRequestRows.length})</Nav.Link>
                                                        </Nav.Item>
                                                    </Nav>
                                                </ScrollingTab>
                                            </Tab.Container>
                                            {crewTab === 'members' ? (
                                                <>
                                                    <div className='overflow-x-auto theme-scrollbar mt-3'>
                                                        <Table className="theme-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Name</th>
                                                                    <th>Username</th>
                                                                    <th>User Role</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {crewMemberRows.length > 0 ? (
                                                                    crewMemberRows
                                                                        .slice((membersPagination.page - 1) * membersPagination.limit, membersPagination.page * membersPagination.limit)
                                                                        .map((member, idx) => (
                                                                            <tr key={member?._id || idx}>
                                                                                <td>{personName(member)}</td>
                                                                                <td>{member?.userName ? `@${member.userName}` : '-'}</td>
                                                                                <td>{member.crewRole.charAt(0).toUpperCase() + member.crewRole.slice(1)}</td>
                                                                            </tr>
                                                                        ))
                                                                ) : (
                                                                    <tr>
                                                                        <td colSpan={3} className="text-center p-5 fs-5">No Records Found</td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                    <div className="d-flex align-items-center justify-content-end gap-2 flex-wrap mt-1">
                                                        {membersPagination.totalPages > 1 && (
                                                            <CustomPagination
                                                                currentPage={membersPagination.page}
                                                                totalPages={membersPagination.totalPages}
                                                                onPageChange={(page) => setMembersPagination(prev => ({ ...prev, page }))}
                                                                showEntries={false}
                                                            />
                                                        )}
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className='overflow-x-auto theme-scrollbar mt-3'>
                                                        <Table className="theme-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Name</th>
                                                                    <th>Username</th>
                                                                    <th>Requested</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {crewRequestRows.length > 0 ? (
                                                                    crewRequestRows
                                                                        .slice((requestsPagination.page - 1) * requestsPagination.limit, requestsPagination.page * requestsPagination.limit)
                                                                        .map((request, idx) => (
                                                                            <tr key={request?.user?._id || idx}>
                                                                                <td>{personName(request.user)}</td>
                                                                                <td>{request?.user?.userName ? `@${request.user.userName}` : '-'}</td>
                                                                                <td>{request?.requestedAt ? formatDateAsItIs(request.requestedAt) : '-'}</td>
                                                                            </tr>
                                                                        ))
                                                                ) : (
                                                                    <tr>
                                                                        <td colSpan={3} className="text-center p-5 fs-5">
                                                                            {eventData?.joinType === 'request' ? 'No Pending Requests' : 'Join requests only apply to Request to join crews'}
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                    <div className="d-flex align-items-center justify-content-end gap-2 flex-wrap mt-1">
                                                        {requestsPagination.totalPages > 1 && (
                                                            <CustomPagination
                                                                currentPage={requestsPagination.page}
                                                                totalPages={requestsPagination.totalPages}
                                                                onPageChange={(page) => setRequestsPagination(prev => ({ ...prev, page }))}
                                                                showEntries={false}
                                                            />
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ) : (
                                        // For Events - Show all tabs
                                        <Tabs
                                            defaultActiveKey="home"
                                            id="justify-tab-example"
                                            className="mb-3 border-none"
                                            justify
                                        >
                                            <Tab eventKey="home" title={`Member (${Array.isArray(eventData?.members) ? eventData.members.length : 0})`} className='table'>
                                                <div className='overflow-auto'>
                                                    {Array.isArray(eventData?.members) && eventData.members.length > 0 ? (
                                                        <>
                                                            <table className='member-table'>
                                                                <thead>
                                                                    <tr>
                                                                        <th>Name</th>
                                                                        <th>Username</th>
                                                                        <th>Role</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {eventData.members.map((member, idx) => {
                                                                        const memberName = member?.firstName && member?.lastName
                                                                            ? `${member.firstName} ${member.lastName}`.trim()
                                                                            : member?.userName || member?._id || `Member ${idx + 1}`;
                                                                        return (
                                                                            <tr key={member?._id || idx}>
                                                                                <td><strong>{memberName}</strong></td>
                                                                                <td>
                                                                                    {member?.userName && (
                                                                                        <span className="text-muted">@{member.userName}</span>
                                                                                    )}
                                                                                </td>
                                                                                <td>
                                                                                    {member?.role ? (
                                                                                        <span className="badge bg-primary text-white">{member.role.name === "single_user" ? "User" : (member.role.name.charAt(0).toUpperCase() + member.role.name.slice(1))}</span>
                                                                                    ) : (
                                                                                        <span className="badge bg-primary text-white">Member</span>
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                            <div className="d-flex justify-content-center mt-3">
                                                                <CustomPagination
                                                                    currentPage={membersPagination.page}
                                                                    totalPages={membersPagination.totalPages}
                                                                    onPageChange={(page) => setMembersPagination(prev => ({ ...prev, page }))}
                                                                    showEntries={false}
                                                                />
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="text-center py-5">
                                                            <i className="fas fa-users fa-3x text-muted mb-3"></i>
                                                            <h5 className="text-muted">No Members Found</h5>
                                                            <p className="text-muted">This {isCrew ? 'crew' : 'event'} doesn't have any members yet.</p>
                                                        </div>
                                                    )}

                                                </div>
                                            </Tab>
                                            <Tab eventKey="contact" title={`Co-hosts (${coHostCrewsOf(eventData).length})`}>
                                                {isCompany && coHostCrewsOf(eventData).length > 0 ? (
                                                    <>
                                                        <div className="table-responsive">
                                                            <table className="table table-hover">
                                                                <thead className="table-light">
                                                                    <tr>
                                                                        <th>Crew</th>
                                                                        <th>Invited</th>
                                                                        <th style={{ width: '120px' }}>Status</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {coHostCrewsOf(eventData)
                                                                        .slice((staffPagination.page - 1) * staffPagination.limit, staffPagination.page * staffPagination.limit)
                                                                        .map((coHost, idx) => {
                                                                            const coHostCrew = typeof coHost?.crewId === 'object' ? coHost.crewId : null;
                                                                            return (
                                                                                <tr key={coHostCrew?._id || coHost?.crewId || idx}>
                                                                                    <td>
                                                                                        <strong>{coHostCrew?.name || 'Crew'}</strong>
                                                                                    </td>
                                                                                    <td>
                                                                                        <span className="text-muted">{coHost?.invitedAt ? new Date(coHost.invitedAt).toLocaleDateString() : '-'}</span>
                                                                                    </td>
                                                                                    <td>
                                                                                        <span className={`badge ${CO_HOST_STATUS_BADGES[coHost?.status] || 'bg-secondary text-white'}`}>
                                                                                            {coHost?.status ? coHost.status.charAt(0).toUpperCase() + coHost.status.slice(1) : '-'}
                                                                                        </span>
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                        <div className="d-flex justify-content-center mt-3">
                                                            <CustomPagination
                                                                currentPage={staffPagination.page}
                                                                totalPages={staffPagination.totalPages}
                                                                onPageChange={(page) => setStaffPagination(prev => ({ ...prev, page }))}
                                                                showEntries={false}
                                                            />
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="text-center py-5">
                                                        <i className="fas fa-user-tie fa-3x text-muted mb-3"></i>
                                                        <h5 className="text-muted">No Co-host Crews</h5>
                                                        <p className="text-muted">No crew has been invited to co-host this event.</p>
                                                    </div>
                                                )}
                                            </Tab>
                                            <Tab eventKey="profile" title={`Waitlist (${Array.isArray(eventData?.waitlist) ? eventData.waitlist.length : 0})`}>
                                                {Array.isArray(eventData?.waitlist) && eventData.waitlist.length > 0 ? (
                                                    <>
                                                        <div className="table-responsive">
                                                            <table className="table table-hover">
                                                                <thead className="table-light">
                                                                    <tr>
                                                                        <th>Name</th>
                                                                        <th>Username</th>
                                                                        <th>User Role</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {eventData.waitlist.map((waitlistItem, idx) => {
                                                                        const waitlistUser = typeof waitlistItem === 'object' ? waitlistItem : null;
                                                                        const waitlistName = waitlistUser?.firstName && waitlistUser?.lastName
                                                                            ? `${waitlistUser.firstName} ${waitlistUser.lastName}`.trim()
                                                                            : waitlistUser?.userName || waitlistItem || `Waitlist ${idx + 1}`;
                                                                        return (
                                                                            <tr key={waitlistUser?._id || waitlistItem || idx}>
                                                                                <td>
                                                                                    <strong>{waitlistName}</strong>
                                                                                </td>
                                                                                <td>
                                                                                    {waitlistUser?.userName && (
                                                                                        <span className="text-muted">@{waitlistUser.userName}</span>
                                                                                    )}
                                                                                </td>
                                                                                <td>
                                                                                    {waitlistUser?.role ? (
                                                                                        <span className="badge bg-primary text-white">{waitlistUser.role.name === "single_user" ? "User" : (waitlistUser.role.name.charAt(0).toUpperCase() + waitlistUser.role.name.slice(1))}</span>
                                                                                    ) : (
                                                                                        <span className="badge bg-primary text-white">Waitlist</span>
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                        <div className="d-flex justify-content-center mt-3">
                                                            <CustomPagination
                                                                currentPage={waitlistPagination.page}
                                                                totalPages={waitlistPagination.totalPages}
                                                                onPageChange={(page) => setWaitlistPagination(prev => ({ ...prev, page }))}
                                                                showEntries={false}
                                                            />
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="text-center py-5">
                                                        <i className="fas fa-clock fa-3x text-muted mb-3"></i>
                                                        <h5 className="text-muted">No Waitlist Entries</h5>
                                                        <p className="text-muted">This event has no users on the waitlist.</p>
                                                    </div>
                                                )}
                                            </Tab>
                                            <Tab eventKey="longer-tab" title={`Question (${Array.isArray(eventData?.questions) ? eventData.questions.length : 0})`}>
                                                {Array.isArray(eventData?.questions) && eventData.questions.length > 0 ? (
                                                    <>
                                                        <div className="mb-3">
                                                            {eventData.questions.map((q, idx) => {
                                                                const askedByUser = typeof q?.userId === 'object' ? q.userId : null;
                                                                const askedByName = askedByUser?.firstName && askedByUser?.lastName
                                                                    ? `${askedByUser.firstName} ${askedByUser.lastName}`.trim()
                                                                    : askedByUser?.userName || q?.userId || 'Unknown';
                                                                const isAnswered = q?.answeredBy && q?.answer;
                                                                const answeredByUser = typeof q?.answeredBy === 'object' ? q.answeredBy : null;
                                                                const answeredByName = answeredByUser?.firstName && answeredByUser?.lastName
                                                                    ? `${answeredByUser.firstName} ${answeredByUser.lastName}`.trim()
                                                                    : answeredByUser?.userName || q?.answeredBy || 'Unknown';

                                                                return (
                                                                    <div key={q?._id || idx} className="mb-3 p-3 border rounded">
                                                                        <div className="mb-2">
                                                                            <p className="mb-1 text-light fw-semibold">Question:</p>
                                                                            <p className="mb-0 text-light">{q?.question || '-'}</p>
                                                                        </div>
                                                                        <div className="row g-2 mb-2">
                                                                            <div className="col-md-6">
                                                                                <p className="mb-0 text-muted small">
                                                                                    <strong>Asked by:</strong> {askedByName}
                                                                                    {askedByUser?.userName && askedByName !== askedByUser.userName && (
                                                                                        <span className="text-muted ms-1">(@{askedByUser.userName})</span>
                                                                                    )}
                                                                                </p>
                                                                            </div>
                                                                            <div className="col-md-6">
                                                                                <p className="mb-0 text-muted small">
                                                                                    <strong>Asked on:</strong> {q?.createdAt ? formatDateAsItIs(q.createdAt) : '-'}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                        {isAnswered ? (
                                                                            <>
                                                                                <div className="mb-2 p-2 bg-light rounded">
                                                                                    <p className="mb-1 text-success fw-semibold">Answer:</p>
                                                                                    <p className="mb-0 text-dark">{q.answer || '-'}</p>
                                                                                </div>
                                                                                <div className="row g-2">
                                                                                    <div className="col-md-6">
                                                                                        <p className="mb-0 text-success small">
                                                                                            <strong>Answered by:</strong> {answeredByName}
                                                                                            {answeredByUser?.userName && answeredByName !== answeredByUser.userName && (
                                                                                                <span className="text-muted ms-1">(@{answeredByUser.userName})</span>
                                                                                            )}
                                                                                        </p>
                                                                                    </div>
                                                                                    <div className="col-md-6">
                                                                                        <p className="mb-0 text-success small">
                                                                                            <strong>Answered on:</strong> {q.answeredDate ? formatDateAsItIs(q.answeredDate) : '-'}
                                                                                        </p>
                                                                                    </div>
                                                                                </div>
                                                                            </>
                                                                        ) : (
                                                                            <div className="col-12">
                                                                                <span className="badge bg-warning text-dark">Pending</span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                        <div className="d-flex justify-content-center mt-3">
                                                            <CustomPagination
                                                                currentPage={questionsPagination.page}
                                                                totalPages={questionsPagination.totalPages}
                                                                onPageChange={(page) => setQuestionsPagination(prev => ({ ...prev, page }))}
                                                                showEntries={false}
                                                            />
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="text-center py-5">
                                                        <i className="fas fa-question-circle fa-3x text-muted mb-3"></i>
                                                        <h5 className="text-muted">No Questions Found</h5>
                                                        <p className="text-muted">This event doesn't have any questions yet.</p>
                                                    </div>
                                                )}
                                            </Tab>
                                        </Tabs>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default EventDetails;
